import {
  CloudOutlined,
  ExperimentOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  MedicineBoxOutlined,
  ReadOutlined,
  SafetyOutlined,
  SmileOutlined,
  SunOutlined,
  SyncOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import Ribbon from 'assets/ribbon.svg?react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { CSSProperties } from 'react';
import { CategorySummaryDTO } from 'services/api/models/CategoryDTO';

/**
 * The icon of a category: an image uploaded in the admin (SVG) when there is
 * one, else the icon the backend's short key names (set in the admin), else
 * nothing. An unknown key falls back to a book so a typo never breaks a
 * page. Both are sized like an antd icon (1em square).
 */
const ICONS: Record<string, React.ComponentType<{ className?: string; style?: CSSProperties }>> = {
  info: InfoCircleOutlined,
  book: ReadOutlined,
  cycle: SyncOutlined,
  pair: TeamOutlined,
  heart: HeartOutlined,
  sun: SunOutlined,
  lotus: SmileOutlined,
  drop: ExperimentOutlined,
  leaf: MedicineBoxOutlined,
  ribbon: Ribbon,
  shield: SafetyOutlined,
  cloud: CloudOutlined,
};

export default function CategoryIcon({
  icon,
  iconUrl,
  className,
  style,
}: {
  icon: string;
  iconUrl?: string | null;
  className?: string;
  style?: CSSProperties;
}) {
  if (!iconUrl && !icon) return null;
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className={cn('size-[1em] object-contain', className)}
        style={style}
        data-testid="category-icon-image"
      />
    );
  }
  const Icon = ICONS[icon] ?? ReadOutlined;
  // The ribbon is a raw SVG: size it like an antd icon (1em square)
  return <Icon className={cn(Icon === Ribbon && 'size-[1em]', className)} style={style} aria-hidden />;
}

const categoryIconBoxVariants = cva(
  'flex shrink-0 items-center justify-center overflow-hidden rounded-sm text-foreground',
  {
    variants: {
      /** `muted` sits on a white card or row; `card` on a tinted tile. */
      surface: {
        muted: 'bg-muted',
        card: 'bg-card',
      },
      size: {
        sm: 'size-8 text-base',
        default: 'size-9 text-lg',
        lg: 'size-10 text-xl',
      },
    },
    defaultVariants: {
      surface: 'muted',
      size: 'default',
    },
  },
);

/** A category's icon on its square tile, as rows, tiles and page headers show it; nothing without an icon. */
export function CategoryIconBox({
  category,
  size,
  surface,
  className,
}: {
  category: Pick<CategorySummaryDTO, 'icon' | 'icon_url'>;
  className?: string;
} & VariantProps<typeof categoryIconBoxVariants>) {
  if (!category.icon_url && !category.icon) return null;
  return (
    <span className={cn(categoryIconBoxVariants({ size, surface }), className)}>
      <CategoryIcon icon={category.icon} iconUrl={category.icon_url} />
    </span>
  );
}
